import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { NameIdOption } from "../../../../../common/types/commonTypes";
import { getCoreUserFullName } from "../../../../../common/utils/users";
import {
  fetchExpertUsers,
  selectAllExpertUsers,
  shouldFetchExpertUsers,
} from "../../../../../features/user/expertUsersSlice";
import { ExpertUser } from "../../../../../features/user/types/entities";
import {
  NO_HR_REPRESENTATIVE_ID,
  buildExpertOptions,
} from "./expertsAutocompleteUtils";

const useExpertUsersAutocomplete = (
  includeNoHrRep = false,
  includeExpert?: (expert: ExpertUser) => boolean,
) => {
  const { t } = useTranslation("talentProfile");
  const dispatch = useAppDispatch();
  const experts = useAppSelector(selectAllExpertUsers);
  const { status, lastFetched } = useAppSelector((state) => state.expertUsers);

  const noHrRepOption: NameIdOption = useMemo(
    () => ({ id: NO_HR_REPRESENTATIVE_ID, name: t("noHrRep") }),
    [t],
  );

  useEffect(() => {
    if (shouldFetchExpertUsers({ lastFetched, status })) {
      dispatch(fetchExpertUsers({}));
    }
  }, [dispatch, lastFetched, status]);

  const expertOptions = useMemo(() => {
    const visible = includeExpert ? experts.filter(includeExpert) : experts;
    return buildExpertOptions(
      visible.map((expert) => ({
        id: expert.id,
        name: getCoreUserFullName(expert),
      })),
      includeNoHrRep,
      noHrRepOption,
    );
  }, [experts, includeExpert, includeNoHrRep, noHrRepOption]);

  return {
    expertOptions,
    isLoading: status === "pending" && lastFetched == null,
  };
};

export default useExpertUsersAutocomplete;
